import _defineProperty from "C:\\Users\\fgrassinger\\Documents\\Arbeitsprojekte\\InteractiveLegend_Matrix\\node_modules\\@babel\\runtime/helpers/esm/defineProperty";
import { COLOR, FILL, FILLOPACITY, OPACITY, SHAPE, SIZE, STROKE, STROKEOPACITY, STROKEWIDTH } from '../../channel';
import { getTypedFieldDef, isFieldDef, isTimeFormatFieldDef, title as fieldDefTitle } from '../../channeldef';
import { LEGEND_PROPERTIES, VG_LEGEND_PROPERTIES } from '../../legend';
import { GEOJSON } from '../../type';
import { deleteNestedProperty, getFirstDefined, keys } from '../../util';
import { mergeTitleComponent, numberFormat } from '../common';
import { guideEncodeEntry } from '../guide';
import { isUnitModel } from '../model';
import { parseGuideResolve } from '../resolve';
import { defaultTieBreaker, makeImplicit, mergeValuesWithExplicit } from '../split';
import { LegendComponent } from './component';
import * as encode from './encode';
import * as properties from './properties';
import { direction, type } from './properties';
export function parseLegend(model) {
  if (isUnitModel(model)) {
    model.component.legends = parseUnitLegend(model);
  } else {
    model.component.legends = parseNonUnitLegend(model);
  }
}

function parseUnitLegend(model) {
  var encoding = model.encoding;
  return [COLOR, FILL, STROKE, STROKEWIDTH, SIZE, SHAPE, OPACITY, FILLOPACITY, STROKEOPACITY].reduce(function (legendComponent, channel) {
    var def = encoding[channel];

    if (model.legend(channel) && model.getScaleComponent(channel) && !(isFieldDef(def) && channel === SHAPE && def.type === GEOJSON)) {
      legendComponent[channel] = parseLegendForChannel(model, channel);
    }

    return legendComponent;
  }, {});
}

function getLegendDefWithScale(model, channel) {
  var scale = model.scaleName(COLOR);

  if (channel === 'color') {
    return model.markDef.filled ? {
      fill: scale
    } : {
      stroke: scale
    };
  }

  return _defineProperty({}, channel, model.scaleName(channel));
}

function isExplicit(value, property, legend, fieldDef) {
  switch (property) {
    case 'values':
      // specified legend.values is already respected, but may get transformed.
      return !!legend.values;

    case 'title':
      // title can be explicit if fieldDef.title is set
      if (property === 'title' && value === fieldDef.title) {
        return true;
      }

  } // Otherwise, things are explicit if the returned value matches the specified property


  return value === legend[property];
}

export function parseLegendForChannel(model, channel) {
  var fieldDef = model.fieldDef(channel);
  var legend = model.legend(channel);
  var legendCmpt = new LegendComponent({}, getLegendDefWithScale(model, channel));
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = LEGEND_PROPERTIES[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var property = _step.value;
      var value = getProperty(property, legend, channel, model);

      if (value !== undefined) {
        var explicit = isExplicit(value, property, legend, fieldDef);

        if (explicit || model.config.legend[property] === undefined) {
          legendCmpt.set(property, value, explicit);
        }
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  var legendEncoding = legend.encoding || {};
  var legendEncode = ['labels', 'legend', 'title', 'symbols', 'gradient'].reduce(function (e, part) {
    var legendEncodingPart = guideEncodeEntry(legendEncoding[part] || {}, model);
    var value = encode[part] ? encode[part](fieldDef, legendEncodingPart, model, channel, legendCmpt) // apply rule
    : legendEncodingPart; // no rule -- just default values

    if (value !== undefined && keys(value).length > 0) {
      e[part] = {
        update: value
      };
    }

    return e;
  }, {});

  if (keys(legendEncode).length > 0) {
    legendCmpt.set('encode', legendEncode, !!legend.encoding);
  }

  return legendCmpt;
}

function getProperty(property, legend, channel, model) {
  var encoding = model.encoding,
      mark = model.mark;
  var fieldDef = getTypedFieldDef(encoding[channel]);
  var legendConfig = model.config.legend;
  var timeUnit = fieldDef.timeUnit;
  var scaleType = model.getScaleComponent(channel).get('type');

  switch (property) {
    case 'format':
      // We don't include temporal field here as we apply format in encode block
      if (isTimeFormatFieldDef(fieldDef)) {
        return undefined;
      }

      return numberFormat(fieldDef, legend.format, model.config);

    case 'formatType':
      // Same as format, We don't include temporal field here as we apply format in encode block
      if (isTimeFormatFieldDef(fieldDef)) {
        return undefined;
      }

      return legend.formatType;

    case 'title':
      return fieldDefTitle(fieldDef, model.config, {
        allowDisabling: true
      }) || undefined;

    case 'type':
      return type({
        legend: legend,
        channel: channel,
        timeUnit: timeUnit,
        scaleType: scaleType,
        alwaysReturn: false
      });

    case 'direction':
      return direction({
        legend: legend,
        legendConfig: legendConfig,
        timeUnit: timeUnit,
        channel: channel,
        scaleType: scaleType
      });
    // TODO: enable when https://github.com/vega/vega/issues/1351 is fixed
    // case 'clipHeight':
    //   return getFirstDefined(specifiedLegend.clipHeight, properties.clipHeight(properties.type(...)));

    case 'labelOverlap':
      return getFirstDefined(legend.labelOverlap, properties.defaultLabelOverlap(scaleType));

    case 'gradientLength':
      return getFirstDefined( // do specified gradientLength first
      legend.gradientLength, legendConfig.gradientLength, // Otherwise, use smart default based on plot height
      properties.defaultGradientLength({
        model: model,
        legend: legend,
        legendConfig: legendConfig,
        channel: channel,
        scaleType: scaleType
      }));

    case 'symbolType':
      return getFirstDefined(legend.symbolType, properties.defaultSymbolType(mark));

    case 'values':
      return properties.values(legend, fieldDef);
  } // Otherwise, return specified property.


  return legend[property];
}

function parseNonUnitLegend(model) {
  var _model$component = model.component,
      legends = _model$component.legends,
      resolve = _model$component.resolve;
  var _iteratorNormalCompletion2 = true;
  var _didIteratorError2 = false;
  var _iteratorError2 = undefined;

  try {
    var _loop = function _loop() {
      var child = _step2.value;
      parseLegend(child);
      keys(child.component.legends).forEach(function (channel) {
        resolve.legend[channel] = parseGuideResolve(model.component.resolve, channel);

        if (resolve.legend[channel] === 'shared') {
          // If the resolve says shared (and has not been overridden)
          // We will try to merge and see if there is a conflict
          legends[channel] = mergeLegendComponent(legends[channel], child.component.legends[channel]);

          if (!legends[channel]) {
            // If merge returns nothing, there is a conflict so we cannot make the legend shared.
            // Thus, mark legend as independent and remove the legend component.
            resolve.legend[channel] = 'independent';
            delete legends[channel];
          }
        }
      });
    };

    for (var _iterator2 = model.children[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
      _loop();
    }
  } catch (err) {
    _didIteratorError2 = true;
    _iteratorError2 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
        _iterator2.return();
      }
    } finally {
      if (_didIteratorError2) {
        throw _iteratorError2;
      }
    }
  }

  keys(legends).forEach(function (channel) {
    var _iteratorNormalCompletion3 = true;
    var _didIteratorError3 = false;
    var _iteratorError3 = undefined;

    try {
      for (var _iterator3 = model.children[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
        var child = _step3.value;

        if (!child.component.legends[channel]) {
          // skip if the child does not have a particular legend
          continue;
        }

        if (resolve.legend[channel] === 'shared') {
          // After merging shared legend, make sure to remove legend from child
          delete child.component.legends[channel];
        }
      }
    } catch (err) {
      _didIteratorError3 = true;
      _iteratorError3 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion3 && _iterator3.return != null) {
          _iterator3.return();
        }
      } finally {
        if (_didIteratorError3) {
          throw _iteratorError3;
        }
      }
    }
  });
  return legends;
}

export function mergeLegendComponent(mergedLegend, childLegend) {
  if (!mergedLegend) {
    return childLegend.clone();
  }

  var mergedOrient = mergedLegend.getWithExplicit('orient');
  var childOrient = childLegend.getWithExplicit('orient');

  if (mergedOrient.explicit && childOrient.explicit && mergedOrient.value !== childOrient.value) {
    // TODO: throw warning if resolve is explicit (We don't have info about explicit/implicit resolve yet.)
    // Cannot merge due to inconsistent orient
    return undefined;
  }

  var typeMerged = false; // Otherwise, let's merge

  var _iteratorNormalCompletion4 = true;
  var _didIteratorError4 = false;
  var _iteratorError4 = undefined;

  try {
    var _loop2 = function _loop2() {
      var prop = _step4.value;
      var mergedValueWithExplicit = mergeValuesWithExplicit(mergedLegend.getWithExplicit(prop), childLegend.getWithExplicit(prop), prop, 'legend', // Tie breaker function
      function (v1, v2) {
        switch (prop) {
          case 'symbolType':
            return mergeSymbolType(v1, v2);

          case 'title':
            return mergeTitleComponent(v1, v2);

          case 'type':
            // There are only two types. If we have different types, then prefer symbol over gradient.
            typeMerged = true;
            return makeImplicit('symbol');
        }

        return defaultTieBreaker(v1, v2, prop, 'legend');
      });
      mergedLegend.setWithExplicit(prop, mergedValueWithExplicit);
    };

    for (var _iterator4 = VG_LEGEND_PROPERTIES[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
      _loop2();
    }
  } catch (err) {
    _didIteratorError4 = true;
    _iteratorError4 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion4 && _iterator4.return != null) {
        _iterator4.return();
      }
    } finally {
      if (_didIteratorError4) {
        throw _iteratorError4;
      }
    }
  }

  if (typeMerged) {
    if (((mergedLegend.implicit || {}).encode || {}).gradient) {
      deleteNestedProperty(mergedLegend.implicit, ['encode', 'gradient']);
    }

    if (((mergedLegend.explicit || {}).encode || {}).gradient) {
      deleteNestedProperty(mergedLegend.explicit, ['encode', 'gradient']);
    }
  }

  return mergedLegend;
}

function mergeSymbolType(st1, st2) {
  if (st2.value === 'circle') {
    // prefer "circle" over "stroke"
    return st2;
  }

  return st1;
}